import React, { useState, useEffect } from 'react';
import { stateService } from '../core/StateService.js';
import { registry } from '../admin/designer/core/Registry.js';
import '../admin/designer/widgets/index.js'; // Stellt sicher, dass alle Widgets im Frontend geladen und registriert werden

// Generiert dynamisch echtes CSS aus den Widget-Daten (für Responsive Breakpoints)
const generateCSS = (nodes) => {
    let css = '';
    const traverse = (nodeList) => {
        if (!nodeList) return;
        nodeList.forEach(node => {
            if (node.style) {
                // Desktop (Basis)
                if (node.style.desktop) {
                    const rules = Object.entries(node.style.desktop)
                        .filter(([k, v]) => v !== undefined && v !== null && v !== '')
                        .map(([k,v]) => `${k.replace(/([A-Z])/g, "-$1").toLowerCase()}: ${v};`)
                        .join(' ');
                    if (rules) css += `\n#${node.id} { ${rules} }`;
                }
                // Tablet (<= 1024px)
                if (node.style.tablet) {
                    const rules = Object.entries(node.style.tablet)
                        .filter(([k, v]) => v !== undefined && v !== null && v !== '')
                        .map(([k,v]) => `${k.replace(/([A-Z])/g, "-$1").toLowerCase()}: ${v};`)
                        .join(' ');
                    if (rules) css += `\n@media (max-width: 1024px) { #${node.id} { ${rules} } }`;
                }
                // Mobile (<= 768px)
                if (node.style.mobile) {
                    const rules = Object.entries(node.style.mobile)
                        .filter(([k, v]) => v !== undefined && v !== null && v !== '')
                        .map(([k,v]) => `${k.replace(/([A-Z])/g, "-$1").toLowerCase()}: ${v};`)
                        .join(' ');
                    if (rules) css += `\n@media (max-width: 768px) { #${node.id} { ${rules} } }`;
                }
            }
            if (node.children) traverse(node.children);
        });
    };
    traverse(nodes);
    return css;
};

// Rekursiver Renderer für TEF Designer-Knoten im Live-Frontend
const FrontendWidget = ({ node }) => {
    const config = registry.getWidget(node.type);
    if (!config) {
        return <div style={{display:'none'}}>Unknown Widget: {node.type}</div>;
    }
    
    const Component = config.component;
    const customClasses = node.advanced?.cssClasses || '';
    const customId = node.advanced?.cssId || node.id;
    
    // Wir wrappen das Component in einen Div mit der node.id, 
    // damit das generierte CSS via ID (#node-id) darauf greifen kann.
    return (
        <div id={node.id} className={customClasses} data-widget-type={node.type}>
            <Component node={node} isLiveMode={true}>
                {node.children && node.children.map(child => (
                    <FrontendWidget key={child.id} node={child} />
                ))}
            </Component>
        </div>
    );
};

export function PageRenderer({ pageId }) {
    const [pageData, setPageData] = useState(null);
    const [headerData, setHeaderData] = useState(null);
    const [footerData, setFooterData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            setLoading(true);
            const data = await stateService.fetchPublicPage(pageId);
            
            const settings = stateService.getAdminSettings();
            let hData = null, fData = null;
            if (settings.globalHeaderTemplate) {
                hData = await stateService.fetchPublicTemplate(settings.globalHeaderTemplate);
            }
            if (settings.globalFooterTemplate) {
                fData = await stateService.fetchPublicTemplate(settings.globalFooterTemplate);
            }

            if (!isMounted) return;
            if (data && data.data) {
                setPageData(data);
                setHeaderData(hData);
                setFooterData(fData);
                setError(false);
            } else {
                setError(true);
            }
            setLoading(false);
        };
        load();
        
        return () => { isMounted = false; };
    }, [pageId]);

    if (loading) {
        return (
            <div className="tef-live-frontend" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#08081a', position: 'relative', overflow: 'hidden' }}>
                <div className="modal-liquid-bg">
                  <div className="liquid-blob liquid-blob-1"></div>
                  <div className="liquid-blob liquid-blob-2"></div>
                </div>
                <div className="edit-lightbox-content premium-modal" style={{ padding: '40px', textAlign: 'center', position: 'relative', zIndex: 10, maxWidth: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div className="modal-border-glow"></div>
                  <div style={{ width: 40, height: 40, border: '3px solid rgba(6,182,212,0.2)', borderTopColor: '#06b6d4', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' }} />
                  <p className="premium-label" style={{ color: '#fff', fontSize: '1.1rem', margin: 0 }}>Lade Seite "{pageId}"...</p>
                </div>
            </div>
        );
    }

    if (error || !pageData || !pageData.data || !pageData.data.content) {
        return (
            <div className="tef-live-frontend" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#08081a', position: 'relative', overflow: 'hidden' }}>
                <div className="modal-liquid-bg">
                  <div className="liquid-blob liquid-blob-1" style={{background: 'radial-gradient(circle, rgba(168,85,247,0.4) 0%, rgba(8,8,26,0) 70%)'}}></div>
                  <div className="liquid-blob liquid-blob-2" style={{background: 'radial-gradient(circle, rgba(236,72,153,0.3) 0%, rgba(8,8,26,0) 70%)'}}></div>
                  <div className="liquid-blob liquid-blob-3" style={{background: 'radial-gradient(circle, rgba(6,182,212,0.3) 0%, rgba(8,8,26,0) 70%)'}}></div>
                </div>
                
                <div className="edit-lightbox-content premium-modal" style={{ padding: '60px 40px', textAlign: 'center', position: 'relative', zIndex: 10, maxWidth: '500px' }}>
                  <div className="modal-border-glow"></div>
                  <div style={{ fontSize: '84px', marginBottom: '20px', fontWeight: 900, background: 'linear-gradient(135deg, #00d4ff, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 0 30px rgba(168,85,247,0.4))' }}>
                    404
                  </div>
                  <h2 className="premium-title" style={{ fontSize: '28px', marginBottom: '16px', justifyContent: 'center' }}>Seite nicht gefunden</h2>
                  <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '32px', fontSize: '1.1rem', lineHeight: '1.6' }}>
                    Die aufgerufene Seite <span style={{fontWeight: 600, color: '#06b6d4'}}>{pageId}</span> ist aktuell nicht veröffentlicht oder existiert nicht.
                  </p>
                  
                  <a href="/" style={{ textDecoration: 'none', display: 'flex', justifyContent: 'center' }}>
                    <button type="button" className="premium-btn save" style={{ width: '100%', maxWidth: '300px', display: 'flex', justifyContent: 'center' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width: '20px', height: '20px', marginRight: '8px'}}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                      </svg>
                      Zurück zur Startseite
                      <div className="btn-glow"></div>
                    </button>
                  </a>
                </div>
            </div>
        );
    }

    const contentNodes = pageData.data.content;
    const allNodes = [
        ...(headerData?.data?.content || []),
        ...contentNodes,
        ...(footerData?.data?.content || [])
    ];
    
    // Generiere CSS für Header, Content und Footer
    const dynamicCss = generateCSS(allNodes);

    return (
        <div className="tef-page-renderer" style={{ width: '100%', minHeight: '100vh', background: '#ffffff', overflowX: 'hidden' }}>
            {/* Dynamisch genereriertes CSS für die Designer-Elemente injizieren */}
            <style>{dynamicCss}</style>

            {headerData?.data?.content && headerData.data.content.map(node => (
                <FrontendWidget key={`h-${node.id}`} node={node} />
            ))}

            {/* Recursives Rendering des JSON-Baums */}
            {contentNodes.map(node => (
                <FrontendWidget key={node.id} node={node} />
            ))}

            {footerData?.data?.content && footerData.data.content.map(node => (
                <FrontendWidget key={`f-${node.id}`} node={node} />
            ))}
        </div>
    );
}

export default PageRenderer;
